import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Spinner from '@/components/basic/Spinner';
import {defaultQueryOptions} from '@/pages/_utils';
import {useInfiniteList} from '@refinedev/core';
import {ConversationContext} from '../types';
import {Button} from '@/shadcn_components/ui/button';
import {Send} from 'lucide-react';
import {useMutation} from '@tanstack/react-query';
import {apiService} from '@/lib/ApiService';
import useLoginStore from '@/store/useLoginStore';
import TextareaInput from '@/components/refinedev/TextareaInput';
import CommentInputForm from './comment-input-form';
import {formatDistance} from 'date-fns';
import {Avatar, AvatarFallback, AvatarImage} from '@/shadcn_components/ui/avatar';
import {CommentSectionHeader} from './comment-section-header';

export const CommentSection = () => {
  const {activeConversation} = useContext(ConversationContext);
  const userIdentity = useLoginStore(state => state.userdata);

  const messageResults = useInfiniteList({
    resource: `conversations/${activeConversation?.id}/messages`,
    sorters: [{field: 'updated_at', order: 'desc'}],
    pagination: {pageSize: 10},
    // filters: [{field: 'conversation_id', operator: 'eq', value: activeConversation?.id}],
    queryOptions: {
      ...defaultQueryOptions,
      enabled: !!activeConversation, // only fetch when conversation_id is available
    },
    errorNotification: false,
    meta: {with: ['user']},
  });

  const {fetchNextPage, isFetchingNextPage, hasNextPage} = messageResults;

  const messagesRef = useRef<HTMLDivElement>(null);

  const messages = useMemo(() => {
    if (!messageResults.data) return [];
    const data = messageResults.data.pages.map(page => page.data);
    const results = ([] as any[]).concat(...data);
    results.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1));
    console.log(results);
    return results;
  }, [messageResults.data]);

  const [interaction, setInteraction] = React.useState<any>(undefined);

  useEffect(() => {
    // first page loaded, scroll to bottom
    if (messagesRef.current && interaction === 'next-page') {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
      setInteraction(undefined);
    }
  }, [activeConversation, messageResults.data, interaction]);

  const sendMessage = useMutation({
    mutationKey: ['sendMessage'],
    mutationFn: async ({conversation_id, message}: any) => {
      await apiService.conversations.sendMessage(conversation_id, message);
    },
  });

  const handleSendMessage = useCallback(
    data => {
      const inputMessage = data.message?.trim();
      console.log('inputMessage', inputMessage);
      if (inputMessage && activeConversation) {
        sendMessage.mutate(
          {conversation_id: activeConversation?.id, message: inputMessage},
          {
            onSuccess: data => {
              console.log('data', data);
              messageResults.refetch();
            },
          },
        );
      }
    },
    [activeConversation, sendMessage],
  );

  const handleEdit = useCallback(() => {
    // Handle edit conversation
    console.log('Edit conversation:', activeConversation);
  }, [activeConversation]);

  const handleDelete = useCallback(() => {
    // Handle delete conversation
    console.log('Delete conversation:', activeConversation);
  }, [activeConversation]);

  const handleClose = useCallback(() => {
    // Handle close conversation
    console.log('Close conversation:', activeConversation);
  }, [activeConversation]);

  // returns the JSX for the ChatMessageView component
  if (!activeConversation) return null;

  if (messageResults.isLoading) {
    return <Spinner />;
  }

  return (
    <div className="h-full overflow-y-auto scroll">
      <CommentSectionHeader conversation={activeConversation} />
      <div ref={messagesRef} className="">
        <div className="h-20 bg-white border-t p-2">
          <CommentInputForm onSubmit={handleSendMessage} isLoading={sendMessage.isLoading} />
        </div>
        <div className="flex flex-col gap-2 p-2">
          {messages.map((message, index) => (
            <CommentCard key={message.id} comment={message} currentUser={userIdentity} />
          ))}
        </div>
        {!hasNextPage ? (
          <p className="self-center">-----</p>
        ) : (
          <Button
            className="self-center"
            onClick={() => {
              fetchNextPage();
              setInteraction('next-page');
            }}
            disabled={isFetchingNextPage}>
            {isFetchingNextPage && <Spinner className="self-center" />} Tải thêm
          </Button>
        )}
      </div>
    </div>
  );
};

interface CommentCardProps {
  comment: any;

  currentUser: any;
}

export const CommentCard = ({comment, currentUser}: CommentCardProps) => {
  return (
    <div className={'flex flex-col gap-1'} id={`comment-${comment.id}`}>
      <div className={'flex gap-4'}>
        <Avatar className={'w-[32px] h-[32px]'}>
          <AvatarImage src={comment.user?.avatarUrl} />
          <AvatarFallback>CN</AvatarFallback>
        </Avatar>
        <div className={`flex flex-col w-full`}>
          <div className={'min-h-[30px] rounded-lg s-comment-card border'}>
            <div
              className={
                'bg-gray-100 h-[37px] w-full user rounded-t-lg flex items-center justify-between border-b'
              }>
              <div className={'flex flex-row items-center gap-2 px-3 '}>
                <div className={'font-semibold'}>{comment.user?.name}</div>
                <div className={'text-opacity-80'}>
                  {formatDistance(Date.now(), comment.created_at, {addSuffix: true})}
                </div>
              </div>
            </div>
            <div className="p-3">{comment.message}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
